/*
 * kemi-product-3d.js — Tarro 3D en vivo (Three.js vanilla), port de la capa R3F de
 * la landing Next.js (Scene.tsx + Product/CreatinaModel + ScrollChoreography).
 *
 * Custom element <kemi-product-3d> fijo detrás del contenido (pointer-events:none):
 *  - Renderer alpha + cámara [0,0,6] fov 35, luces (key/rim/lime/ambient).
 *  - Entorno de estudio via RoomEnvironment (sustituye al studio.hdr, 0 KB).
 *  - Carga el .glb (URL desde media 3D del producto o setting), lo normaliza a
 *    altura 2.1 y oscurece sus materiales PBR (env 0.2 · color 0.4 · rough +0.25).
 *  - Coreografía por scroll sobre Hero→CTA con las MISMAS keyframes (desktop/móvil),
 *    intro "emerge" al cargar y flotación idle sinusoidal.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

const HERO_Y = -1.9;
const TARGET_HEIGHT = 2.1;
const ENV_INTENSITY = 0.2;
const DARKEN = 0.4;
const ROUGHNESS_BOOST = 0.25;
const PI = Math.PI;

/* Keyframes de la coreografía (tiempo de timeline 0..2.85, igual que el GSAP original).
   Cada track es [t, valor]; se interpola con easeInOutQuad (= power1.inOut). */
const TRACKS = {
  desktop: {
    x: [[0, 0], [0.85, -2.1], [1.0, -2.1], [1.85, 1.55], [2.0, 1.55], [2.85, 0]],
    y: [[0, HERO_Y], [0.85, 0], [2.0, 0], [2.85, 0.7]],
    z: [[0, 0], [1.0, 0], [1.85, 1.7], [2.0, 1.7], [2.85, 0]],
    ry: [[0, 0], [0.85, PI * 0.85], [1.0, PI * 0.85], [1.85, PI * 1.15], [2.0, PI * 1.15], [2.85, PI * 2]],
    s: [[0, 1], [1.0, 1], [1.85, 1.3], [2.0, 1.3], [2.85, 0.72]],
  },
  mobile: {
    x: [[0, 0], [2.85, 0]],
    y: [[0, HERO_Y], [0.85, 0], [2.0, 0], [2.85, 0.85]],
    z: [[0, 0], [1.0, 0], [1.85, 1.1], [2.0, 1.1], [2.85, 0]],
    ry: [[0, 0], [0.85, PI * 0.85], [1.0, PI * 0.85], [1.85, PI * 1.15], [2.0, PI * 1.15], [2.85, PI * 2]],
    s: [[0, 1], [1.0, 1], [1.85, 1.12], [2.0, 1.12], [2.85, 0.72]],
  },
};
const TIMELINE_END = 2.85;

const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/** Interpola un track [t,val] en tiempo T con easing por segmento. */
function evalTrack(track, T) {
  if (T <= track[0][0]) return track[0][1];
  if (T >= track[track.length - 1][0]) return track[track.length - 1][1];
  for (let i = 0; i < track.length - 1; i++) {
    const [t0, v0] = track[i];
    const [t1, v1] = track[i + 1];
    if (T >= t0 && T <= t1) {
      if (t1 === t0) return v1;
      const local = easeInOutQuad((T - t0) / (t1 - t0));
      return v0 + (v1 - v0) * local;
    }
  }
  return track[track.length - 1][1];
}

class KemiProduct3D extends HTMLElement {
  connectedCallback() {
    this.modelUrl = this.getAttribute('model-url') || '';
    this.startSel = this.getAttribute('start-selector') || '#hero';
    this.endSel = this.getAttribute('end-selector') || '#cta';
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.modelUrl) {
      console.warn('[kemi-product-3d] Sin model-url: agrega el .glb como media 3D del producto o en el setting de la sección.');
      return;
    }

    this._initThree();
    this._loadModel();

    this._onResize = () => this._resize();
    this._onScroll = () => { this._needsRender = true; };
    window.addEventListener('resize', this._onResize, { passive: true });
    window.addEventListener('scroll', this._onScroll, { passive: true, capture: true });

    this._start = performance.now();
    this._raf = requestAnimationFrame((t) => this._tick(t));
  }

  disconnectedCallback() {
    cancelAnimationFrame(this._raf);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('scroll', this._onScroll, { capture: true });
    if (this.renderer) this.renderer.dispose();
  }

  _initThree() {
    const canvas = document.createElement('canvas');
    canvas.className = 'kemi-3d__canvas';
    this.appendChild(canvas);

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.5 : 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer = renderer;

    const scene = new THREE.Scene();
    this.scene = scene;

    const camera = new THREE.PerspectiveCamera(35, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.set(0, 0, 6);
    this.camera = camera;

    // Entorno de estudio (reflejos de relleno) sin depender de un .hdr.
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

    // Iluminación: idéntica a Scene.tsx.
    scene.add(new THREE.AmbientLight(0xffffff, 0.22));
    const key = new THREE.DirectionalLight(0xffffff, 1.1);
    key.position.set(3.5, 5.5, 4);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0xffffff, 1.5);
    rim.position.set(-4, 3, -6);
    scene.add(rim);
    const lime = new THREE.DirectionalLight(0xc4d200, 0.3);
    lime.position.set(6, -1, 2);
    scene.add(lime);

    // Grupos: exterior (scroll) + interior (idle), como Product.tsx.
    this.scrollGroup = new THREE.Group();
    this.idleGroup = new THREE.Group();
    this.scrollGroup.add(this.idleGroup);
    scene.add(this.scrollGroup);
    this.scrollGroup.position.set(0, HERO_Y, 0);

    this._addDust();
    this._needsRender = true;
  }

  _addDust() {
    const COUNT = 380;
    const positions = new Float32Array(COUNT * 3);
    for (let i = 0; i < COUNT; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 12;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 10;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 6 - 2;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({
      color: 0xc4d200,
      size: 0.02,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.dust = new THREE.Points(geo, mat);
    this.scene.add(this.dust);
  }

  _loadModel() {
    new GLTFLoader().load(
      this.modelUrl,
      (gltf) => {
        const model = gltf.scene;
        // Normaliza: centra en el origen y escala a TARGET_HEIGHT de alto.
        const box = new THREE.Box3().setFromObject(model);
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(center);
        const scale = size.y > 0 ? TARGET_HEIGHT / size.y : 1;
        model.position.sub(center);
        model.scale.setScalar(scale);
        model.position.multiplyScalar(scale);

        // Oscurece los materiales PBR (env, color, roughness) como CreatinaModel.
        model.traverse((child) => {
          if (!child.isMesh) return;
          const m = child.material;
          if (!m || !m.isMeshStandardMaterial) return;
          m.envMapIntensity = (m.envMapIntensity ?? 1) * ENV_INTENSITY;
          m.roughness = Math.min(1, m.roughness + ROUGHNESS_BOOST);
          m.color.multiplyScalar(DARKEN);
        });

        this.idleGroup.add(model);
        this._needsRender = true;
      },
      undefined,
      (err) => console.error('[kemi-product-3d] Error cargando el modelo:', err)
    );
  }

  /** Progreso 0..1 del scroll sobre el rango Hero→CTA (start top top → bottom bottom).
      Rect-based: funciona aunque el scroll sea de un contenedor interno (Horizon en
      desktop scrollea .page-wrapper, con window.scrollY siempre en 0). */
  _progress() {
    const startEl = document.querySelector(this.startSel);
    const endEl = document.querySelector(this.endSel);
    if (!startEl || !endEl) return 0;
    const startTop = startEl.getBoundingClientRect().top;
    const rangeHeight = endEl.getBoundingClientRect().bottom - startTop;
    const denom = rangeHeight - window.innerHeight;
    if (denom <= 0) return 0;
    return Math.min(Math.max(-startTop / denom, 0), 1);
  }

  _resize() {
    if (!this.renderer) return;
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this._needsRender = true;
  }

  _tick(now) {
    this._raf = requestAnimationFrame((t) => this._tick(t));
    const t = (now - this._start) / 1000;

    const tracks = window.matchMedia('(min-width: 768px)').matches ? TRACKS.desktop : TRACKS.mobile;
    const p = this.reduced ? 0 : this._progress();
    const T = p * TIMELINE_END;

    // Pose por scroll.
    const sg = this.scrollGroup;
    let x = evalTrack(tracks.x, T);
    let y = evalTrack(tracks.y, T);
    const z = evalTrack(tracks.z, T);
    const ry = evalTrack(tracks.ry, T);
    let s = evalTrack(tracks.s, T);

    // Intro "emerge": los primeros 1.4 s sube desde más abajo y crece desde 0.6.
    if (!this.reduced && t < 1.4) {
      const e = easeOutCubic(t / 1.4);
      y += (1 - e) * -1.8;
      s *= 0.6 + 0.4 * e;
    }

    sg.position.set(x, y, z);
    sg.rotation.y = ry;
    sg.scale.setScalar(s);

    // Flotación idle (grupo interior), independiente del scroll.
    if (!this.reduced) {
      this.idleGroup.position.y = Math.sin(t / 1.3) * 0.07;
      this.idleGroup.rotation.z = Math.sin(t / 1.7) * 0.04;
    }

    // Parallax sutil del polvo con el scroll.
    if (this.dust) this.dust.rotation.y = p * 0.6;

    // Render continuo mientras hay animación (intro/idle); si reduce-motion, solo on-demand.
    if (this.reduced && !this._needsRender) return;
    this._needsRender = false;
    this.renderer.render(this.scene, this.camera);
  }
}

if (!customElements.get('kemi-product-3d')) {
  customElements.define('kemi-product-3d', KemiProduct3D);
}
